import { NavBar } from 'components/NavBar/NavBar'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { Pagination } from 'components/Pagination/Pagination'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import useQueryPagination from 'hooks/useQueryPagination'
import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import { RootState } from 'store'
import { EditContainer, Flex, MainHeadline, PageFooter, PageWrapper } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { OtherText } from 'styling/Typography'
import { formatDateForDisplay, getParams, handlePagination } from 'utils/helpers'
import { InventoryTransferModal } from './InventoryTransferModal'
import { clearInventoryTransfers, getInventoryTransfers } from 'features/inventory/inventoryTransfersSlice'
import { ViewInventoryTransferModal } from './ViewInventoryTransferModal'
import { ArrowLeft } from 'assets/icons/ArrowLeft'
import { DemoBadge } from 'pages/Operations/Shared/OpsSharedComponents'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import {
    IInventoryTransferControlOverrideMap,
    IInventoryTransferRow,
} from 'types/inventoryControl.interface'
import {
    INVENTORY_DEMO_TRANSFERS,
    TRANSFER_RECEIPT_OK_NOTE,
    TRANSFER_STATUS_META,
} from '../inventoryControlDemoData'
import { TransferRefText } from './InventoryTransfers.styled'

export const InventoryTransfers = () => {
    const { transfers, totalPages } = useSelector((store: RootState) => store.inventoryTransfers)
    const [params, setParams] = useState<any>(getParams({ location }))
    const [modal, setModal] = useState(false)
    const [viewId, setViewId] = useState<number | null>(null)
    const [controlOverrides, setControlOverrides] = useState<IInventoryTransferControlOverrideMap>({})

    useQueryPagination({ fetchFunctions: [getInventoryTransfers], cleanUpFunctions: [clearInventoryTransfers], params })

    const usingDemo = !transfers.length && USE_OPS_MOCKS && INVENTORY_DEMO_TRANSFERS.length > 0

    const rows: IInventoryTransferRow[] = useMemo(() => {
        const base: IInventoryTransferRow[] = transfers.length ? transfers : INVENTORY_DEMO_TRANSFERS
        return base.map((row) => (controlOverrides[row.id] ? { ...row, control: controlOverrides[row.id] } : row))
    }, [transfers, controlOverrides])

    const viewRow = rows.find((row) => row.id === viewId) || null

    const handleConfirmReceipt = (row: IInventoryTransferRow) => {
        if (!row.control) return
        setControlOverrides((prev) => ({
            ...prev,
            [row.id]: {
                ...row.control,
                status: 'RECEIVED',
                receivedAt: format(new Date(), 'd MMM HH:mm'),
                receivedBy: 'Milos Mladenovic',
                varianceNote: TRANSFER_RECEIPT_OK_NOTE,
            },
        }))
        toast.success(`Receipt confirmed — TRF-${row.id} received at ${row.toLocation ? row.toLocation.name : 'destination'}`)
    }

    const columns: IHonestTableColumn<IInventoryTransferRow>[] = [
        { id: 'ref', header: 'REF', cell: (item) => <TransferRefText>{`TRF-${item.id}`}</TransferRefText> },
        { id: 'date', header: 'DATE', cell: (item) => formatDateForDisplay(item.date, true) },
        { id: 'assignedEmployee', header: 'ASSIGNED EMPLOYEE', cell: (item) => item.assignedForUser ? (item.assignedForUser?.firstName + ' ' + item.assignedForUser?.lastName) : '/' },
        { id: 'fromLocation', header: 'FROM LOCATION', cell: (item) => item.fromLocation ? item.fromLocation.name : '/' },
        { id: 'toLocation', header: 'TO LOCATION', cell: (item) => item.toLocation ? item.toLocation.name : '/' },
        { id: 'items', header: 'ITEMS', cell: (item) => item.lines.length || '/' },
        {
            id: 'status',
            header: 'STATUS',
            cell: (item) => {
                if (!item.control) return <OtherText grey>—</OtherText>
                const meta = TRANSFER_STATUS_META[item.control.status]
                return (
                    <StatusPill status={meta.tone} dot={item.control.status === 'IN_TRANSIT'}>
                        {meta.label}
                    </StatusPill>
                )
            },
        },
        { id: 'notes', header: 'NOTES', cell: (item) => item.notes || '/' },
        {
            id: 'actions',
            header: '',
            align: 'right',
            cell: (item) => (
                <span onClick={(e) => e.stopPropagation()}>
                    <EditContainer onClick={() => setViewId(item.id)} >
                        <Flex style={{ transform: 'rotate(180deg)' }} className='arrow-table' >
                            <ArrowLeft />
                        </Flex>
                    </EditContainer>
                </span>
            ),
        },
    ]

    return <>
        <NavBar />
        <PageWrapper>
            <Flex between center mb='16' >
                <Flex column gap='0.25rem'>
                    <Flex center gap='0.75rem'>
                        <MainHeadline>Transfers</MainHeadline>
                        {usingDemo && <DemoBadge />}
                    </Flex>
                    <OtherText grey>Stock movements between locations — receipt confirmed at destination</OtherText>
                </Flex>
                <Flex gap='1rem' >
                    <Button variant='primary' onClick={() => setModal(true)} >
                        Add Transfer
                    </Button>
                </Flex>

            </Flex>
            {!rows.length ? (
                <NoItems title='Transfers' />
            ) : (
                <HonestTable
                    columns={columns}
                    data={rows}
                    keyField='id'
                    onClick={(item) => setViewId(item.id)}
                />
            )}
            {modal && <InventoryTransferModal closeModal={() => setModal(false)} />}
            {viewRow && (
                <ViewInventoryTransferModal
                    value={viewRow}
                    closeModal={() => setViewId(null)}
                    onConfirmReceipt={usingDemo ? handleConfirmReceipt : undefined}
                />
            )}
        </PageWrapper>
        <PageFooter>
            <Pagination
                setPage={(page) => handlePagination(page, setParams, 30)}
                pageCount={Math.ceil(totalPages)}
                forcePage={(params.page ? params.page : 1) - 1}
            />
        </PageFooter>
    </>
}
